import { motion } from "framer-motion"
import { formatDate } from "lopermut"
import { InfoIcon, RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { CardContent } from "../../molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../molecules/Chart"
import { cn } from "../../utils"

const ACTIVITY_BAR_SIZE = 20

type NPSDatum = {
  date?: string | null
  count?: number
  score?: number | null
  promoters?: number
  passives?: number
  detractors?: number
}

type Props = {
  data?: NPSDatum[]
  isPending: boolean
  chartConfig: ChartConfig
  tooltipInfo?: string
  noDataT?: {
    title: string
    description: string
  }
  action?: ReactNode
  chartClassName?: string
}

export const NpsActivityChart = ({
  data,
  isPending,
  chartConfig,
  tooltipInfo,
  noDataT,
  action,
  chartClassName,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const noData = useMemo(() => {
    if (isPending) return false
    return !data || !data.length || data?.every((item) => item.count === 0)
  }, [data])

  const computedData = useMemo(() => {
    return data?.map((item) => {
      let formattedDate = null
      try {
        formattedDate = item.date ? formatDate(item.date, "MMM d") : null
      } catch (_error) {
        // ignore formatting errors
      }

      return {
        date: formattedDate,
        count: item.count,
        score: item.score,
        promoters: item.promoters,
        passives: item.passives,
        detractors: item.detractors,
      }
    })
  }, [data])

  const fallbackData = useMemo(() => {
    if (!noData) return []
    const days = 30
    return Array.from({ length: days }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (days - 1 - i))
      const formattedDate = formatDate(date, "MMM d")

      const promoters = 20 + ((i + Math.pow(-10, i)) % 4)
      const passives = 30 + ((i + Math.pow(-10, i)) % 5)
      const detractors = 18 + ((i + Math.pow(-10, i)) % 4)
      const count = promoters + passives + detractors
      const rawScore = count > 0 ? ((promoters - detractors) / count) * 100 : 0
      const score = Math.max(-100, Math.min(100, Math.round(rawScore)))

      return {
        date: formattedDate,
        count: count,
        score: score,
        promoters: promoters,
        passives: passives,
        detractors: detractors,
      }
    })
  }, [noData])

  const chartData = noData ? fallbackData : computedData

  // Compute max count for stacked bars domain (counts)
  const countDomain = useMemo(() => {
    if (!chartData?.length) return [0, 0] as [number, number]
    const maxCount = chartData.reduce((max, d) => {
      const total = (d.promoters ?? 0) + (d.passives ?? 0) + (d.detractors ?? 0) || (d.count ?? 0)
      return Math.max(max, total)
    }, 0)
    return [0, Math.ceil(maxCount * 1.1)] as [number, number]
  }, [chartData])

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  return (
    <div className="flex flex-col w-full relative rounded-none border-none gap-2">
      {noData && (
        <div className="flex items-center justify-center absolute inset-0 z-10 p-4">
          <div className="flex flex-col gap-2 py-14 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-md text-foreground text-stroke">{noDataT?.title}</h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground text-stroke-sm">{noDataT?.description}</p>
            </motion.div>
          </div>
        </div>
      )}
      <CardContent
        className={cn(
          "flex flex-col p-0 gap-2 items-end",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <div className={cn("flex flex-row justify-between gap-4 pr-4", !action && "justify-end")}>
          {action}
          <NPSLegend
            className={cn(noData && "pointer-events-none grayscale opacity-15")}
            chartConfig={chartConfig}
            tooltipInfo={tooltipInfo}
          />
        </div>
        <div ref={containerRef} className="w-full overflow-x-auto pb-2 -mb-2">
          <ChartContainer
            config={chartConfig}
            className={cn("aspect-auto h-[180px]", chartClassName)}
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <ComposedChart accessibilityLayer data={chartData} margin={{ bottom: 0, top: 5 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={30}
              />
              {/* Left axis for counts (stacked bars) */}
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                width={30}
                domain={countDomain}
                allowDataOverflow
                hide
              />
              {/* Right axis for NPS score line (-100 to 100) */}
              <YAxis
                yAxisId="score"
                orientation="right"
                domain={[-100, 100]}
                width={30}
                tick={false}
                axisLine={false}
                hide
              />
              <ChartTooltip
                content={(props: TooltipContentProps) => {
                  return <ChartTooltipContent {...props} />
                }}
              />
              {/* Stacked bars: detractors (bottom), passives, promoters (top) */}
              <Bar
                name={"detractors"}
                dataKey="detractors"
                stackId="count"
                fill={`var(--negative)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"passives"}
                dataKey="passives"
                stackId="count"
                fill={`var(--neutral)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"promoters"}
                dataKey="promoters"
                stackId="count"
                fill={`var(--positive)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              {/* NPS score line on secondary axis */}
              <Line
                type="monotone"
                name={"score"}
                dataKey="score"
                yAxisId="score"
                stroke="var(--chart-4)"
                strokeWidth={2}
                dot={false}
                strokeDasharray="2 2"
                connectNulls
              />
            </ComposedChart>
          </ChartContainer>
        </div>
      </CardContent>
    </div>
  )
}

const NPSLegend = ({
  className,
  chartConfig,
  tooltipInfo,
}: {
  className?: string
  chartConfig: ChartConfig
  tooltipInfo?: string
}) => {
  const items: Array<{ key: string; label: string; colorVar: string }> = [
    {
      key: "detractors",
      label: (chartConfig["detractors"]?.label as string) || "Detractors",
      colorVar: "var(--negative)",
    },
    {
      key: "passives",
      label: (chartConfig["passives"]?.label as string) || "Passives",
      colorVar: "var(--neutral)",
    },
    {
      key: "promoters",
      label: (chartConfig["promoters"]?.label as string) || "Promoters",
      colorVar: "var(--positive)",
    },
    {
      key: "score",
      label: (chartConfig["score"]?.label as string) || "Score",
      colorVar: "var(--chart-4)",
    },
  ]
  return (
    <div className={cn("flex items-center gap-4 flex-wrap", className)}>
      {items.map(({ key, label, colorVar }) => (
        <div key={key} className="flex items-center gap-1.5">
          <div className="size-2 rounded-full" style={{ background: colorVar }} />
          <span className="text-sm text-muted-foreground">{label}</span>
        </div>
      ))}
      {tooltipInfo && (
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="size-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipInfo}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
